// Storage utilities - Local storage only for now
// Cloud sync can be added later once the app is working

class Storage {
  constructor() {
    this._currentUser = null;
  }

  setCurrentUser(user) {
    this._currentUser = user;
  }

  // Local storage helpers
  _getLocal(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Local get error:', error);
      return null;
    }
  }

  _setLocal(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Local set error:', error);
      return false;
    }
  }

  _deleteLocal(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Local delete error:', error);
      return false;
    }
  }

  // Solves
  async saveSolves(solves) {
    const data = JSON.stringify(solves);
    this._setLocal('solvesmarter-solves', data);
    return true;
  }

  async loadSolves() {
    try {
      const data = this._getLocal('solvesmarter-solves');
      if (data) {
        const solves = JSON.parse(data);
        return solves.map(solve => ({
          ...solve,
          date: new Date(solve.date)
        }));
      }
      return [];
    } catch (error) {
      console.error('Failed to load solves:', error);
      return [];
    }
  }

  // Learned Algorithms
  async saveLearnedAlgs(algs) {
    const data = JSON.stringify(algs);
    this._setLocal('solvesmarter-learned-algs', data);
    return true;
  }

  async loadLearnedAlgs() {
    try {
      const data = this._getLocal('solvesmarter-learned-algs');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Failed to load learned algorithms:', error);
      return {};
    }
  }

  // User Level
  async saveUserLevel(level) {
    this._setLocal('solvesmarter-user-level', level);
    return true;
  }

  async loadUserLevel() {
    try {
      const data = this._getLocal('solvesmarter-user-level');
      return data || 'beginner';
    } catch (error) {
      return 'beginner';
    }
  }

  // Inspection Enabled
  async saveInspectionEnabled(enabled) {
    this._setLocal('solvesmarter-inspection-enabled', enabled.toString());
    return true;
  }

  async loadInspectionEnabled() {
    try {
      const data = this._getLocal('solvesmarter-inspection-enabled');
      if (data === null) return true; // Default to true
      return data === 'true';
    } catch (error) {
      return true; // Default to true
    }
  }

  // Algorithm Times
  async saveAlgTimes(algTimes) {
    const data = JSON.stringify(algTimes);
    this._setLocal('solvesmarter-alg-times', data);
    return true;
  }

  async loadAlgTimes() {
    try {
      const data = this._getLocal('solvesmarter-alg-times');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      return {};
    }
  }

  // Practice Times
  async savePracticeTimes(practiceTimes) {
    const data = JSON.stringify(practiceTimes);
    this._setLocal('solvesmarter-practice-times', data);
    return true;
  }

  async loadPracticeTimes() {
    try {
      const data = this._getLocal('solvesmarter-practice-times');
      return data ? JSON.parse(data) : { cross: [], f2l: [], oll: [], pll: [] };
    } catch (error) {
      return { cross: [], f2l: [], oll: [], pll: [] };
    }
  }

  // Completed Drills
  async saveCompletedDrills(completedDrills) {
    const data = JSON.stringify(completedDrills);
    this._setLocal('solvesmarter-completed-drills', data);
    return true;
  }

  async loadCompletedDrills() {
    try {
      const data = this._getLocal('solvesmarter-completed-drills');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      return {};
    }
  }

  // Clear all data
  async clearAllData() {
    this._deleteLocal('solvesmarter-solves');
    this._deleteLocal('solvesmarter-learned-algs');
    this._deleteLocal('solvesmarter-user-level');
    this._deleteLocal('solvesmarter-inspection-enabled');
    this._deleteLocal('solvesmarter-alg-times');
    this._deleteLocal('solvesmarter-practice-times');
    this._deleteLocal('solvesmarter-completed-drills');
    return true;
  }

  // Export data
  async exportData() {
    const solves = await this.loadSolves();
    const learnedAlgs = await this.loadLearnedAlgs();
    const userLevel = await this.loadUserLevel();
    const inspectionEnabled = await this.loadInspectionEnabled();
    const algTimes = await this.loadAlgTimes();
    const practiceTimes = await this.loadPracticeTimes();
    const completedDrills = await this.loadCompletedDrills();
    
    return {
      version: '2.0',
      exportDate: new Date().toISOString(),
      data: {
        solves,
        learnedAlgs,
        userLevel,
        inspectionEnabled,
        algTimes,
        practiceTimes,
        completedDrills
      }
    };
  }

  // Import data
  async importData(exportedData) {
    try {
      if (!exportedData || !exportedData.data) {
        throw new Error('Invalid data format');
      }
      
      const { solves, learnedAlgs, userLevel, inspectionEnabled, algTimes, practiceTimes, completedDrills } = exportedData.data;
      
      if (solves) await this.saveSolves(solves);
      if (learnedAlgs) await this.saveLearnedAlgs(learnedAlgs);
      if (userLevel) await this.saveUserLevel(userLevel);
      if (inspectionEnabled !== undefined) await this.saveInspectionEnabled(inspectionEnabled);
      if (algTimes) await this.saveAlgTimes(algTimes);
      if (practiceTimes) await this.savePracticeTimes(practiceTimes);
      if (completedDrills) await this.saveCompletedDrills(completedDrills);
      
      return true;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }

  // Leaderboard stub - returns empty for now (cloud feature)
  async getLeaderboard() {
    return [];
  }

  async saveToLeaderboard() {
    return false;
  }

  async getDisplayName() {
    return null;
  }

  async saveDisplayName() {
    return false;
  }
}

export const storage = new Storage();
